import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/refunds")({
  head: () => ({
    meta: [
      { title: "Refund Policy - Smartasaurus" },
      { name: "description", content: "Our refund and EU right-of-withdrawal policy for Smartasaurus digital and physical products." },
      { property: "og:title", content: "Refund Policy - Smartasaurus" },
      { property: "og:description", content: "Fair, EU-compliant refunds. Here's how it works." },
      { property: "og:url", content: "https://smartasaurus.com/refunds" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Refund Policy - Smartasaurus" },
      { name: "twitter:description", content: "Fair, EU-compliant refunds. Here's how it works." },
    ],
    links: [{ rel: "canonical", href: "https://smartasaurus.com/refunds" }],
  }),
  component: RefundsPage,
});

function RefundsPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-16 md:px-8 md:py-20">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· Legal</div>
        <h1 className="mt-3 font-display text-4xl uppercase leading-[1] md:text-6xl">Refund Policy</h1>
        <p className="mt-3 text-sm text-muted-foreground">Last updated: June 2026</p>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-muted-foreground">
          <section>
            <h2 className="font-display text-xl uppercase text-foreground">Summary</h2>
            <p className="mt-2">Smartasaurus is currently free to use. Newsletter, quizzes, articles and collections cost nothing and have no refunds because there is no purchase. This policy will apply once digital or physical products launch.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">1. EU right of withdrawal (14 days)</h2>
            <p className="mt-2">As a consumer in the EU/EEA, you generally have 14 days from the day you receive a physical product - or from the day of purchase for digital products - to withdraw from the contract without giving a reason.</p>
            <p className="mt-2">To use this right, email <a href="mailto:support@smartasaurus.com" className="text-primary underline-offset-4 hover:underline">support@smartasaurus.com</a> with your order number and the words "I withdraw from my purchase." We will refund the price (including standard delivery) within 14 days using the original payment method.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">2. Digital products & instant downloads</h2>
            <p className="mt-2">For digital downloads (e.g. challenge decks, packs), the right of withdrawal expires once delivery has started <span className="text-foreground">with your express consent and acknowledgment that you lose the withdrawal right</span>. We will ask for this explicit confirmation at checkout. If you have not started the download, your 14-day right still applies.</p>
            <p className="mt-2">If a digital product is defective or materially different from what was advertised, you are entitled to a free repair, replacement, price reduction, or refund.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">3. Physical products</h2>
            <p className="mt-2">Return unused physical products to us within 14 days of receipt for a full refund of the product and standard delivery. Return shipping is paid by the buyer unless the product was faulty or wrongly sent.</p>
            <p className="mt-2">Under Swedish consumer law, you have a <span className="text-foreground">3-year</span> right to complain about a faulty product (reklamation). Contact us as soon as you notice the issue.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">4. How to request a refund</h2>
            <ol className="mt-2 list-decimal space-y-2 pl-5">
              <li>Email <a href="mailto:support@smartasaurus.com" className="text-primary underline-offset-4 hover:underline">support@smartasaurus.com</a> with your order number and reason.</li>
              <li>We confirm within 3 business days.</li>
              <li>Refunds are issued to the original payment method within 14 days of approval.</li>
            </ol>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">5. Dispute resolution</h2>
            <p className="mt-2">If we can't resolve a dispute directly, you can contact the Swedish National Board for Consumer Disputes (ARN) at <a href="https://www.arn.se/" target="_blank" rel="noopener noreferrer" className="text-primary underline-offset-4 hover:underline">arn.se</a>. The EU's Online Dispute Resolution platform closed on 20 July 2025 and is no longer available.</p>
          </section>

          <p className="pt-4">
            See also: <Link to="/privacy" className="text-primary underline-offset-4 hover:underline">Privacy</Link> · <Link to="/terms" className="text-primary underline-offset-4 hover:underline">Terms</Link> · <Link to="/contact" className="text-primary underline-offset-4 hover:underline">Contact</Link>
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
